import React, { useEffect, useState } from "react";
import moment from "moment";
import { UpcomingCashback } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { Collapsible } from "../Collapsible";
import { FeatureFlags, useFeatureFlag } from "../../feature-flags/FeatureFlagContext";
import { t } from "../../i18n";
import "./UserUpcomingV2CashbacksComponent.scss";

interface Props {
  userId: number;
}

// HttpService rejects with a plain { message, status } object, not an Error instance, so reach
// for that message before falling back; a raw object stringifies to the useless "[object Object]".
function errorMessage(err: unknown): string {
  if (err != null && typeof err === "object" && typeof (err as { message?: unknown }).message === "string") {
    return (err as { message: string }).message;
  }
  return err instanceof Error ? err.message : String(err);
}

// scheduledPayoutTime is typed Date but arrives as JSON (epoch millis or ISO string); a blank cell
// is preferable to moment's "Invalid date" when the value is absent or unparseable. Rendered in UTC
// to match the cashback config screens, where the payout hour is configured and labelled in UTC.
function formatPayoutTime(value: UpcomingCashback["scheduledPayoutTime"]): string {
  if (value == null) {
    return "";
  }
  const parsed = moment.utc(value as unknown as moment.MomentInput);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "";
}

export function UserUpcomingV2CashbacksComponent({ userId }: Props) {
  const cashbackV2Enabled = useFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED);

  const [cashbacks, setCashbacks] = useState<UpcomingCashback[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!cashbackV2Enabled) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    getContainer()
      .getUpcomingCashbackService()
      .getForUser(userId)
      .then((items) => {
        if (!cancelled) {
          setCashbacks(items ?? []);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(errorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cashbackV2Enabled, userId]);

  if (!cashbackV2Enabled) {
    return null;
  }

  return (
    <Collapsible
      title={t("cashback.upcoming-v2.card-title", undefined, "Upcoming Cashbacks (v2)")}
      collapsed={true}
    >
      <div className="container-fluid">
        {error && (
          <div
            className="alert alert-danger"
            data-testid="user-upcoming-v2-cashback-error"
          >
            {t("cashback.upcoming-v2.error", undefined, "Failed to load upcoming cashbacks.")}
          </div>
        )}

        {!error && (
          <table
            className="table table-striped uv2c-table"
            data-testid="user-upcoming-v2-cashback-table"
          >
            <thead>
              <tr>
                <th>{t("cashback.upcoming-v2.config", undefined, "Config")}</th>
                <th>{t("cashback.upcoming-v2.amount", undefined, "Amount")}</th>
                <th>{t("cashback.upcoming-v2.currency", undefined, "Currency")}</th>
                <th>{t("cashback.upcoming-v2.payout-time", undefined, "Pays at (UTC)")}</th>
              </tr>
            </thead>
            <tbody>
              {cashbacks.map((cashback, index) => (
                <tr
                  key={index}
                  data-testid="user-upcoming-v2-cashback-row"
                >
                  <td>{cashback.configName ?? cashback.configId ?? ""}</td>
                  <td>{cashback.amount ?? ""}</td>
                  <td>{cashback.currency ?? ""}</td>
                  <td>{formatPayoutTime(cashback.scheduledPayoutTime)}</td>
                </tr>
              ))}
              {!loading && cashbacks.length === 0 && (
                <tr data-testid="user-upcoming-v2-cashback-empty">
                  <td colSpan={4}>{t("cashback.upcoming-v2.none", undefined, "No upcoming v2 cashbacks for this player.")}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Collapsible>
  );
}
